import { formatMarkdown } from "@/components/formatMarkdown";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { LinkedinIcon } from "lucide-react";
import { MarketingLink } from "@/components/OptionalLink";

interface TeamProps {
  imageUrl: string;
  name: string;
  description: string;
  position: string;
  linkedIn: string;
  className?: string;
}

const teamList: TeamProps[] = [
  {
    imageUrl: "/ruslan.png",
    name: "Ruslan",
    position: "Co-CEO, Co-Founder",
    description: `
Ruslan is a CTO and full-stack developer experienced in modern web technologies, 
cloud architecture, and AI, with more than 20 years of software development, 
product management, and leading teams. 


With a wealth of hands-on experience in DevOps,
he is looking for ways to simplify deployment and architecture.
`,
    linkedIn: "https://www.linkedin.com/in/ruslanfg/",
    className: "filter grayscale brightness-[1.5]"
  },
  {
    imageUrl: "/steven.png",
    name: "Steven",
    position: "Co-CEO, Co-Founder",
    description: `
  Steven is a serial entrepreneur with a passion for building products that make a difference.

  He has unique ability to combine business and technology to create value and motivate teams to deliver.
    `,
    linkedIn: "https://www.linkedin.com/in/stevenmaurin/",
    className: "filter grayscale brightness-[1.3]"
  },
  {
    imageUrl: "/jonathan.png",
    name: "Jonathan",
    description: `
Jonathan is a multifaceted designer who thrives on crafting comprehensive, 
user-focused design solutions. His expertise spans brand development, 
product design, and web creation, allowing him to seamlessly integrate form and function.
`,
    position: "Advisor, UI & UX Design",
    linkedIn: "https://www.linkedin.com/in/barck/",
    className: "filter grayscale"
  },
  {
    imageUrl: "/maria.png",
    name: "Maria",
    description: `
Maria is responsible for marketing, and community engagement. 
She works closely with partners and manages accelerator programs, 
helping to build relationships and expand the platform's reach.`,
    position: "Founding team",
    linkedIn: "https://www.linkedin.com/in/maria-pylv%C3%A4s-596206130/",
    className: "filter grayscale"
  },
  {
    imageUrl: "/marina.png",
    name: "Marina",
    description: `
Marina is an intern working on development, testing, UI/UX, and marketing. 
She is responsible for the design of the website and landing page, 
bringing creative solutions to enhance user experience and visual appeal.`,
    position: "Founding team",
    linkedIn: "https://www.linkedin.com/in/marina-rush/",
    className: "filter grayscale"
  },
  {
    imageUrl: "/egor.png",
    name: "Egor",
    description: `
Egor is an intern working on testing, UX, and development. 
He is responsible for creating and maintaining templates and documentation, 
ensuring clear communication and consistency across the platform.`,
    position: "Founding team",
    linkedIn: "https://www.linkedin.com/in/egor-gaynutdinov/",
    className: "filter grayscale"
  }
];

export const Team = () => {
  return (
    <section id="team" className="cpx-4 container pt-16 md:pt-24 text-center mb-20">
      <h2 className="font-serif text-[clamp(36px,5.5vw,56px)] leading-[1.05] tracking-tight font-normal mb-4 text-foreground text-balance text-center">
        Our
        <em className="text-primary"> Team</em>
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
        {teamList.map(
          ({ imageUrl, name, position, linkedIn, description, className }: TeamProps) => (
            <Card key={name} className="bg-muted/50 relative flex flex-col">
              <CardHeader className="mt-4 flex items-center flex-col gap-4">
                <MarketingLink rel="noopener noreferrer" target="_blank" href={linkedIn}>
                  <img
                    src={imageUrl}
                    alt={`${name} ${position}`}
                    className={cn(
                      "rounded-full w-24 h-24 aspect-square object-cover bg-white border border-white/40",
                      className
                    )}
                  />
                </MarketingLink>
                <CardTitle className="text-xl flex gap-2 items-center">
                  {name}
                  <MarketingLink
                    rel="noreferrer noopener"
                    href={linkedIn}
                    target="_blank"
                    className="text-secondary-foreground inline-block rounded-full p-2 border-2 border-secondary"
                  >
                    <span className="sr-only">LinkedIn icon</span>
                    <LinkedinIcon size={20} />
                  </MarketingLink>
                </CardTitle>
                <CardDescription>{position}</CardDescription>
                <CardDescription className="text-center prose dark:prose-invert text-balance">
                  {formatMarkdown(description, undefined, undefined, true)}
                </CardDescription>
              </CardHeader>
            </Card>
          )
        )}
      </div>
    </section>
  );
};
